import React, { useState, useEffect, useRef, useMemo } from "react";
import ReactCountryFlag from "react-country-flag";
import PropTypes from "prop-types";
import $ from "jquery";
import { Helmet } from "react-helmet-async";
import {
  Stack,
  Typography,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  Chip,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import LayerdIcon from "../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../icons/detectionLucideIcons";
import { EventsTimeRangePicker } from "../../../common/Greens";
import { StyledMenu } from "../../application/analytics/styled";
import { PeriodItemComponent } from "../../application/analytics/common";
import colors from "../../../../utils/colors";
import V2PageHeader from "../../../common/V2PageHeader";
import SiteScopeGate from "../../../common/SiteScopeGate";
import PermissionGate from "../../../guards/PermissionGate";
import { formatDate } from "../../../../utils/format";
import { getSeverityName } from "../../../../utils/functions";
import { SenseDefenceOperator } from "../../../../utils/constants";
import { tableHeadSx, tableBodySx, getStatusColor, getSeverityConfig } from "./WAFEvent";
import { getWafEventStatus } from "./wafEventListUtils";
import { getDefaultEventListingCustomDateRange } from "./eventTimeRange";
import useEvent from "../../../../hooks/user/useEvent";
import useAuth from "../../../../hooks/useAuth";
import usePermissions from "../../../../hooks/usePermissions";
import useFirewall from "../../../../hooks/user/useFirewall";
import useHome from "../../../../hooks/user/useHome";
import useGlobalSite from "../../../../hooks/user/useGlobalSite";
import EventFilterBar from "./EventFilterBar";
import EventDetailDrawer from "./EventDetailDrawer";
import { buildConditionsFromFilters, getActionFromFilters, withSearchCondition } from "./eventFilterConditions";
import { resolveFirewallSimilarFilter } from "./firewallSimilarEvents";

const SEARCH_DEBOUNCE_MS = 500;

const getOperatorDescription = (operator) => {
  switch (operator) {
    case SenseDefenceOperator.rx:
      return "Regular Expression";
    case SenseDefenceOperator.pm:
      return "Case Insensitive Match";
    case SenseDefenceOperator.ge:
      return "Greater Than";
    case SenseDefenceOperator.eq:
      return "Equals to";
    case SenseDefenceOperator.le:
      return "Less Than";
    default:
      return null;
  }
};

const getFwDetectionDisplay = (item) => {
  const fwType = item?.type && Array.isArray(item.type) && item.type.length > 0 ? item.type[0] : null;
  if (fwType?.attack_type) {
    return {
      title: fwType.attack_type,
      subtitle: fwType.sec_rule_id ? `Rule ${fwType.sec_rule_id}` : null,
      mode: "filled",
    };
  }
  if (item?.rule_details?.length > 0) {
    const rule = item.rule_details[0];
    return {
      title: rule.msg || (rule.ruleId ? `Rule ${rule.ruleId}` : "—"),
      subtitle: rule.ruleId ? `Rule ${rule.ruleId}` : null,
      mode: "filled",
    };
  }
  if (item?.categories_detected?.length > 0) {
    return { title: item.categories_detected[0], subtitle: null, mode: "filled" };
  }
  if (item?.attack_types?.length > 0) {
    return { title: item.attack_types[0], subtitle: null, mode: "filled" };
  }
  if (item?.operator) {
    return {
      title: item.operator,
      subtitle: getOperatorDescription(item.operator),
      mode: "filled",
    };
  }
  return { title: "—", subtitle: null, mode: "empty" };
};

const getFwSeverityConfig = (item) => {
  const fwType = item?.type?.[0];
  if (fwType?.severity != null) {
    return getSeverityConfig(getSeverityName(fwType.severity));
  }
  if (item?.rule_details?.[0]?.severity != null) {
    return getSeverityConfig(getSeverityName(item.rule_details[0].severity));
  }
  if (item?.risk_level) {
    return getSeverityConfig(item.risk_level);
  }
  return { label: "—", color: colors.neutral[700], bgColor: colors.neutral[100] };
};

function SkeletonContent(props) {
  const { rowsPerPage, columnCount = 8 } = props;

  var rowArray = [];
  for (let i = 0; i < rowsPerPage; i++) {
    rowArray.push(i);
  }

  var cellArray = Array.from({ length: columnCount }, (_, i) => i);

  return (
    <TableBody>
      {rowArray.map((r) => (
        <TableRow key={"s_" + r}>
          {cellArray.map((c) => (
            <TableCell
              sx={{
                padding: "16px 8px",
              }}
              key={"s_" + r + "_" + c}
            >
              <Skeleton
                height="22px"
                width="100%"
                py="5px"
                variant="rectangular"
                sx={{
                  borderRadius: "8px",
                }}
              />
            </TableCell>
          ))}
          <TableCell />
        </TableRow>
      ))}
    </TableBody>
  );
}
SkeletonContent.propTypes = {
  rowsPerPage: PropTypes.number.isRequired,
  columnCount: PropTypes.number,
};

const FirewallEventPage = () => {
  const {
    isAuthenticated,
    homeController,
    wafdashController,
    websiteController,
    wafeventController,
    planController,
    setWafEventController,
  } = useAuth();
  const { PERMISSIONS } = usePermissions();
  const {
    fwEvents,
    fwEvent,
    time_range,
    total_fw_events_count,
    // from,
    // rows_per_page,
    getOnlyFwEvents,
    getFwEvents,
    getFwEvent,
    getFwEventStats,
    fwEventLoading,
  } = useEvent();
  const { getTopFwEventsInfo } = useFirewall();
  const { curProjectId } = useHome();
  const { apiSiteId: siteID } = useGlobalSite();

  // Check if user has permission to view firewall events

  const [timeRange, setTimeRange] = useState(time_range.period);
  const [customRangeConfirm, setCustomRangeConfirm] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [customDateRange, setCustomDateRange] = useState(() => getDefaultEventListingCustomDateRange());
  const [activeFilters, setActiveFilters] = useState([]);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isFilteringById, setIsFilteringById] = useState(false);
  const handleFiltersChange = (newFilters) => {
    setActiveFilters(newFilters);
  };

  useEffect(() => {
    const h = setTimeout(() => setQuery(search.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(h);
  }, [search]);

  const getTimeRange = () => {
    if (timeRange === "custom") {
      const now = new Date();
      const timeZone =
        now.getTimezoneOffset() >= 0
          ? "-" + String(now.getTimezoneOffset() / 60).padStart(2, "0") + ":00"
          : "+" + String(now.getTimezoneOffset() / -60).padStart(2, "0") + ":00";
      return { time_zone: timeZone, from: customDateRange[0], to: customDateRange[1] };
    } else {
      return { period: timeRange };
    }
  };

  const selectPeriod = (period) => {
    setAnchorEl(null);
    setTimeRange(period);
    if (period === "custom") {
      $(document).ready(function () {
        $(".react-minimal-datetime-range__range-input-wrapper").click();
        $(".react-minimal-datetime-range").css({
          "margin-top": $(".react-minimal-datetime-range__range-input-wrapper").parent().parent().parent().height() + 4,
        });
      });
    }
  };
  const selectCustomDateRange = (res) => {
    setCustomRangeConfirm(true);
    setCustomDateRange(res);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const apiConditions = useMemo(
    () => withSearchCondition(
      buildConditionsFromFilters(activeFilters.filter((f) => f.id !== "id"), "firewall"),
      query
    ),
    [activeFilters, query]
  );

  const apiAction = useMemo(
    () => getActionFromFilters(activeFilters),
    [activeFilters]
  );

  const getEventsWithFillter = async () => {
    if (homeController) homeController.abort();
    if (wafdashController) wafdashController.abort();
    if (websiteController) websiteController.abort();
    if (wafeventController) wafeventController.abort();
    if (planController) planController.abort();
    
    // Check if there's an ID filter - use dedicated GET endpoint
    const idFilter = activeFilters.find((f) => f.id === "id" && f.value);
    if (idFilter) {
      setIsFilteringById(true);
      setAllEvents([]);
      setHasMore(false);
      isInitialLoadRef.current = false;
      await getFwEvent(idFilter.value);
      return;
    }
    
    setIsFilteringById(false);
    setAllEvents([]);
    setHasMore(true);
    isInitialLoadRef.current = true;
    getTopFwEventsInfo(setWafEventController, siteID, getTimeRange());
    getFwEvents(setWafEventController, siteID, getTimeRange(), ITEMS_PER_PAGE, 0, apiAction, apiConditions);
    getFwEventStats(setWafEventController, siteID, getTimeRange(), apiConditions);
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    if (timeRange === "custom" && customRangeConfirm === false) {
      return;
    }
    setCustomRangeConfirm(false);
    getEventsWithFillter();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, siteID, customDateRange, timeRange, curProjectId, activeFilters, query]);

  const [allEvents, setAllEvents] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const tableContainerRef = useRef(null);
  const sentinelRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const ITEMS_PER_PAGE = 10;

  React.useEffect(() => {
    if (fwEvents !== null && !isFilteringById) {
      if (fwEvents.length === 0 && isInitialLoadRef.current) {
        setAllEvents([]);
        setHasMore(false);
      } else if (fwEvents.length > 0) {
        if (isInitialLoadRef.current) {
          const initialEvents = fwEvents.slice(0, ITEMS_PER_PAGE);
          setAllEvents(initialEvents);
          setHasMore(initialEvents.length === ITEMS_PER_PAGE && fwEvents.length >= ITEMS_PER_PAGE && initialEvents.length < total_fw_events_count);
          isInitialLoadRef.current = false;
        } else {
          setAllEvents((prev) => {
            const newEventsToAdd = fwEvents.slice(0, ITEMS_PER_PAGE);
            const newEvents = [...prev, ...newEventsToAdd];
            setHasMore(newEvents.length < total_fw_events_count);
            return newEvents;
          });
        }
      }
      setIsLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fwEvents, total_fw_events_count, isFilteringById]);

  // Handle single event fetch (when filtering by ID)
  React.useEffect(() => {
    if (isFilteringById) {
      if (fwEvent) {
        // Normalize single event structure to match list format
        const normalizedEvent = { ...fwEvent };
        if (normalizedEvent.type && typeof normalizedEvent.type === "object" && !Array.isArray(normalizedEvent.type)) {
          normalizedEvent.type = [normalizedEvent.type];
        }
        setAllEvents([normalizedEvent]);
      } else {
        setAllEvents([]);
      }
    }
  }, [fwEvent, isFilteringById]);

  React.useEffect(() => {
    setAllEvents([]);
    setHasMore(true);
    isInitialLoadRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteID, customDateRange, timeRange, curProjectId, activeFilters, query]);

  React.useEffect(() => {
    if (!hasMore || isLoadingMore || allEvents.length < ITEMS_PER_PAGE) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const perfectScrollbar = document.querySelector('.ps');
    const root = perfectScrollbar || null;
    const rootMargin = '200px';

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !isLoadingMore && hasMore) {
          if (allEvents.length >= ITEMS_PER_PAGE && allEvents.length < total_fw_events_count) {
            setIsLoadingMore(true);
            const nextFrom = allEvents.length;
            const currentTimeRange = getTimeRange();
            getOnlyFwEvents(siteID, currentTimeRange, ITEMS_PER_PAGE, nextFrom, apiAction, apiConditions);
          }
        }
      },
      {
        root: root,
        rootMargin: rootMargin,
        threshold: 0.1,
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, isLoadingMore, allEvents.length, siteID, timeRange, customDateRange, apiAction, apiConditions, getOnlyFwEvents, total_fw_events_count]);

  const handleEventClick = async (event) => {
    setSelectedEvent(event);
    setDrawerOpen(true);
    if (event.id) {
      await getFwEvent(event.id);
    }
  };

  // Update selectedEvent with full event data when fwEvent is fetched
  React.useEffect(() => {
    if (fwEvent && selectedEvent && fwEvent.id === selectedEvent.id && drawerOpen) {
      setSelectedEvent(fwEvent);
    }
  }, [fwEvent, selectedEvent, drawerOpen]);

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedEvent(null);
  };

  const upsertSimilarFilter = (filters, { id, label, value }) => {
    if (!id || !value) return filters;
    const existingIndex = filters.findIndex((f) => f.id === id);
    if (existingIndex >= 0) {
      const next = [...filters];
      next[existingIndex] = { id, label, value };
      return next;
    }
    return [...filters, { id, label, value }];
  };

  const handleFilterSimilar = (filterType, event) => {
    const resolved = resolveFirewallSimilarFilter(filterType, event);
    if (!resolved?.id || !resolved?.value) return;
    handleFiltersChange(
      upsertSimilarFilter(activeFilters, resolved),
    );
  };
  
  // Show permission denied message if user doesn't have firewall read permission
  return (
    <PermissionGate permission={PERMISSIONS.FIREWALL_READ} resourceLabel="Firewall events">
    <>
      <Helmet title="Firewall Event" />
      <Stack spacing={6} sx={{ p: 6 }}>
        <V2PageHeader title="Firewall Event" sx={{ pb: 0 }} />
        <SiteScopeGate mode="any">
          {() => (
            <>
        <Stack spacing={2}>
          <EventFilterBar
            eventType="firewall"
            activeFilters={activeFilters}
            onFiltersChange={handleFiltersChange}
            events={allEvents}
            search={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search host, URI, IP, UA…"
            searchPending={search.trim() !== query}
            timeRangeComponent={
              <EventsTimeRangePicker
                selectPeriod={selectPeriod}
                selectCustomDateRange={selectCustomDateRange}
                timeRange={timeRange}
                customDateRange={customDateRange}
              />
            }
            onRefresh={getEventsWithFillter}
            refreshing={fwEventLoading}
          />
        </Stack>
        <Stack>
          <Box ref={tableContainerRef} spacing={6} sx={{ border: `solid 1px ${colors.neutral[200]}`, borderRadius: "8px", p: 2 }}>
            <Table
              sx={{
                "& .MuiTableCell-head": { py: 3, px: 3 },
                "& .MuiTableCell-body": { py: 2, px: 3 },
                "& .MuiTableCell-root": { borderBottom: "none" },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ minWidth: 280 }}>
                    <Typography sx={tableHeadSx}>Event ID</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Hostname</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Activity</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>IP address</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Type of detection</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Severity</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Status</Typography>
                  </TableCell>
                  <TableCell sx={{ width: 230 }}>
                    <Typography sx={tableHeadSx}>Timestamp</Typography>
                  </TableCell>
                  <TableCell sx={{ width: 72 }}>
                    <Typography sx={tableHeadSx}>Action</Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              {fwEvents === null && allEvents.length === 0 ? (
                <SkeletonContent rowsPerPage={ITEMS_PER_PAGE} columnCount={8} />
              ) : allEvents.length === 0 ? (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography textAlign="center" sx={tableBodySx}>
                        There are no events
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <TableBody>
                  {allEvents?.map((item, index) => {
                    const status = getWafEventStatus(item);
                    const severityConfig = getFwSeverityConfig(item);
                    const detectionDisplay = getFwDetectionDisplay(item);
                    const eventId = item?.request_id || item?.id;
                    return (
                      <TableRow
                        key={`detail_data_${item?.id || index}`}
                        onClick={() => handleEventClick(item)}
                        sx={{
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: colors.primary[50],
                          },
                        }}
                      >
                        <TableCell>
                          <Typography
                            sx={{
                              ...tableBodySx,
                              fontFamily: "monospace",
                              color: "#1E40AF",
                              wordBreak: "break-all",
                            }}
                          >
                            {eventId || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography sx={tableBodySx}>{item?.host_name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Stack>
                            <Typography sx={tableBodySx}>{item?.method}</Typography>
                            {item?.uri?.length > 36 ? (
                              <Tooltip title={item?.uri} arrow>
                                <Typography sx={{ fontSize: 11, color: "#1E40AF", lineHeight: "14px" }}>
                                  {item?.uri?.substr(0, 36) + " ..."}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography sx={{ fontSize: 11, color: "#1E40AF", lineHeight: "14px" }}>{item?.uri}</Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems={"center"}>
                            {item?.country_iso_code ? (
                              <ReactCountryFlag
                                countryCode={item?.country_iso_code}
                                svg
                                title={item?.country_name}
                                style={{
                                  width: "26px",
                                  height: "18px",
                                }}
                              />
                            ) : null}
                            <Typography sx={{ ...tableBodySx, pl: 2 }}>{item?.src_ip}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.25}>
                            <Typography
                              variant="body1"
                              sx={
                                detectionDisplay.mode === "empty"
                                  ? { ...tableBodySx, color: colors.neutral[500] }
                                  : { ...tableBodySx, fontWeight: 400, color: colors.neutral[1000] }
                              }
                            >
                              {detectionDisplay.title}
                            </Typography>
                            {detectionDisplay.subtitle ? (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: 12,
                                  lineHeight: "18px",
                                  fontWeight: 400,
                                  color: colors.primary[300],
                                }}
                              >
                                {detectionDisplay.subtitle}
                              </Typography>
                            ) : null}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {severityConfig?.label && severityConfig.label !== "—" ? (
                          <Chip
                            size="small"
                            label={severityConfig.label}
                            sx={{
                              px: 1,
                              background: severityConfig?.bgColor || colors.neutral[100],
                              color: severityConfig?.color || colors.neutral[700],
                              borderRadius: "30px",
                              fontSize: 12,
                              fontWeight: "medium",
                            }}
                          />
                          ) : (
                            <Typography sx={tableBodySx}>—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Stack direction={"row"} alignItems={"center"} spacing={2}>
                            <Box
                              style={{
                                width: 12,
                                height: 12,
                                backgroundColor: getStatusColor(status),
                                borderRadius: 100,
                              }}
                            />
                            <Typography sx={tableBodySx}>{status}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography sx={tableBodySx}>{formatDate(item?.timestamp) || "—"}</Typography>
                        </TableCell>
                        <TableCell>
                          <LayerdIcon icon={DETECTION_ICONS.view} size={18} color="neutral" />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              )}
            </Table>
            {hasMore && allEvents.length >= ITEMS_PER_PAGE && (
              <Box ref={sentinelRef} sx={{ height: 1, width: "100%" }} />
            )}
            {isLoadingMore && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {!hasMore && allEvents.length > 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <Typography sx={{ ...tableBodySx, color: colors.neutral[500] }}>
                  No more events to load
                </Typography>
              </Box>
            )}
          </Box>
        </Stack>
            </>
          )}
        </SiteScopeGate>
      </Stack>
      <StyledMenu
        MenuListProps={{ "aria-labelledby": "demo-customized-button" }}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <PeriodItemComponent selectPeriod={selectPeriod} />
      </StyledMenu>
      <EventDetailDrawer 
        open={drawerOpen} 
        onClose={handleDrawerClose} 
        event={selectedEvent} 
        event_Type="firewall"
        onFilterSimilar={handleFilterSimilar}
      />
    </>
    </PermissionGate>
  );
};
export default FirewallEventPage;
